import { useState, useCallback, useRef } from 'react';
import { BubbleData } from './useMapData';

type CountryFeature = GeoJSON.Feature<GeoJSON.MultiPolygon | GeoJSON.Polygon> & {
  id?: string | number;
  properties: {
    name?: string;
    id?: string | number;
    [k: string]: any;
  };
};

export const useMapInteractions = () => {
  const [hovered, setHovered] = useState<CountryFeature | null>(null);
  const [hoveredBubble, setHoveredBubble] = useState<BubbleData | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [scale, setScale] = useState<number>(1);
  
  // Throttling refs for performance
  const lastMouseMove = useRef<number>(0);
  const mouseMoveThrottle = 16; // ~60fps

  // Optimized zoom handler
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.5, Math.min(5, prev * delta)));
  }, []);

  // Reset scale function
  const resetScale = useCallback(() => {
    setScale(1);
  }, []);

  // Throttled mouse move for better performance
  const handleMouseMove = useCallback((e: React.MouseEvent, containerRef: React.RefObject<HTMLDivElement>) => {
    const now = Date.now();
    if (now - lastMouseMove.current < mouseMoveThrottle) return;
    lastMouseMove.current = now;
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  }, [mouseMoveThrottle]);

  // Optimized bubble hover handlers
  const handleBubbleEnter = useCallback((bubble: BubbleData) => {
    setHoveredBubble(bubble);
    setHovered(null); // Clear country hover when hovering bubble
  }, []);

  const handleBubbleLeave = useCallback(() => {
    setHoveredBubble(null);
  }, []);

  // Country hover handlers
  const handleCountryEnter = useCallback((country: CountryFeature) => {
    if (!hoveredBubble) { // Only set if not hovering a bubble
      setHovered(country);
    }
  }, [hoveredBubble]);

  const handleCountryLeave = useCallback(() => {
    setHovered(null);
  }, []);

  return {
    hovered,
    hoveredBubble,
    mousePos,
    scale,
    handleWheel,
    handleMouseMove,
    handleBubbleEnter,
    handleBubbleLeave,
    handleCountryEnter,
    handleCountryLeave,
    resetScale,
  };
};